import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, ChevronDown, ChevronRight, Download,
  ExternalLink, FileText, Loader2, MapPin, Paperclip,
  Pencil, Plus, Shield, TrendingDown, TrendingUp, CheckCircle2,
  Upload, Trash2, Users,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { Switch } from '../../../foundation/altzero_base/base/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../foundation/altzero_base/base/components/ui/dialog';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { organisationService } from '../services/organisationService';
import { siteService } from '../services/siteService';
import { attachmentService } from '../services/attachmentService';
import { billService } from '../services/billService';
import { BillReportScreen } from '../components/bills/BillReportScreen';
import { isClientShielded } from '../utils/shieldPlatinum';
import { useBackTarget } from '../utils/backTarget';
import type { BvBill, ShadowBillResult, ActualBillResult } from '../types/billTypes';
import { useBillVerdicts } from '../hooks/useBillVerdicts';
import { ATTACHMENT_CATEGORY_LABELS } from '../types/billValidationTypes';
import type {
  BvOrganisation,
  BvContact, BvContactFormData, BvContactType,
  BvSite, BvAttachment, BvAttachmentCategory,
} from '../types/billValidationTypes';
import { effectiveInvoiceResult } from '../lib/effectiveInvoice';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  onboarding: 'bg-blue-100 text-blue-800',
  lead: 'bg-gray-100 text-gray-600',
};

const SITE_STATUS_STYLES: Record<string, string> = {
  Enrolled: 'bg-green-100 text-green-800',
  'Pending Enrolment': 'bg-yellow-100 text-yellow-800',
  'Not Enrolled': 'bg-gray-100 text-gray-600',
  Cancelled: 'bg-red-100 text-red-700',
};

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 text-right max-w-[60%] break-words">
        {value || <span className="text-gray-300 font-normal">—</span>}
      </dd>
    </div>
  );
}

// ─── Contact Dialog (small modal — contacts are simple) ──────────────────────

const EMPTY_CONTACT: BvContactFormData = {
  first_name: '', last_name: '', email: '', phone: '',
  mobile: '', contact_type: 'Primary', is_primary: false,
};

function ContactDialog({
  open, onClose, clientId, editing, onSaved,
}: {
  open: boolean; onClose: () => void; clientId: string;
  editing: BvContact | null; onSaved: () => void;
}) {
  const [form, setForm] = useState<BvContactFormData>(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      first_name: editing.first_name, last_name: editing.last_name,
      email: editing.email ?? '', phone: editing.phone ?? '',
      mobile: editing.bv_extn?.mobile ?? '',
      contact_type: editing.bv_extn?.contact_type ?? 'Primary',
      is_primary: editing.bv_extn?.is_primary ?? false,
    } : EMPTY_CONTACT);
  }, [open, editing]);

  const set = <K extends keyof BvContactFormData>(k: K, v: BvContactFormData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.first_name.trim()) return;
    setSaving(true);
    if (editing) await organisationService.updateContact(editing.id, form);
    else await organisationService.createContact(clientId, form);
    onSaved();
    onClose();
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? 'Edit Contact' : 'Add Contact'}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="mt-1" /></div>
            <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} className="mt-1" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile ?? ''} onChange={(e) => set('mobile', e.target.value)} placeholder="+614…" className="mt-1" /></div>
          </div>
          <div>
            <Label>Contact Type</Label>
            <Select value={form.contact_type ?? 'Primary'} onValueChange={(v) => set('contact_type', v as BvContactType)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Primary">Primary</SelectItem>
                <SelectItem value="Billing">Billing</SelectItem>
                <SelectItem value="Technical">Technical</SelectItem>
                <SelectItem value="Broker">Broker</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
            <Label>Primary Contact</Label>
            <Switch checked={form.is_primary ?? false} onCheckedChange={(v) => set('is_primary', v)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.first_name.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Reached from a site's header link? Offer the way back to that site. Reached
  // from the Clients list, a new tab, or a pasted URL? The client list (job 16).
  const back = useBackTarget({ label: 'Back to Clients', to: '/bill-validation/organisations' });

  // Lifecycle controls (job 28): deactivate is the reversible option for a client
  // no longer being validated; delete is for one created by mistake.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deps, setDeps] = useState<{ sites: number; contacts: number; bills: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  const [org, setOrg] = useState<BvOrganisation | null>(null);
  const [contacts, setContacts] = useState<BvContact[]>([]);
  const [sites, setSites] = useState<BvSite[]>([]);
  const [attachments, setAttachments] = useState<BvAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactDialog, setContactDialog] = useState<{ open: boolean; editing: BvContact | null }>({ open: false, editing: null });
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<BvAttachmentCategory>('other');
  const [orgBills, setOrgBills] = useState<BvBill[]>([]);
  const verdictFor = useBillVerdicts(orgBills);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const [downloadingBillId, setDownloadingBillId] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    const [orgRes, contactsRes, sitesRes] = await Promise.all([
      organisationService.getById(id),
      organisationService.listContacts(id),
      siteService.listByClient(id),
    ]);
    if (!orgRes.success) { setError(orgRes.error ?? 'Failed to load'); setLoading(false); return; }
    setOrg(orgRes.data ?? null);
    setContacts(contactsRes.data ?? []);
    const loadedSites = sitesRes.data ?? [];
    setSites(loadedSites);
    if (loadedSites.length > 0) {
      const attRes = await attachmentService.list({ siteId: loadedSites[0].id });
      if (attRes.success) setAttachments(attRes.data ?? []);
      // Load bills for all sites — attach site data so NMI is available in the report rows
      setReportsLoading(true);
      const billResults = await Promise.all(
        loadedSites.map(async s => {
          const res = await billService.listBySite(s.id);
          return (res.data ?? []).map(b => ({ ...b, site: s }));
        })
      );
      setOrgBills(billResults.flat());
      setReportsLoading(false);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Counts are fetched when the dialog opens, not on page load — nobody needs
  // three extra queries just for visiting a client.
  const openDeleteDialog = () => {
    if (!id) return;
    setLifecycleError(null);
    setDeps(null);
    setConfirmDelete(true);
    organisationService.countDependents(id).then((r) => {
      if (r.success && r.data) setDeps(r.data);
      else setLifecycleError(r.error ?? 'Could not check what references this client');
    });
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setLifecycleError(null);
    const res = await organisationService.delete(id);
    setDeleting(false);
    if (!res.success) { setLifecycleError(res.error ?? 'Delete failed'); return; }
    setConfirmDelete(false);
    navigate('/bill-validation/organisations');
  };

  const handleToggleActive = async () => {
    if (!id || !org) return;
    const activating = org.bill_val !== 'active';
    if (!activating && !confirm(
      'Deactivate this client for Bill Validation?\n\n'
      + 'It will be hidden from the Bill Validation lists. Nothing is deleted, '
      + 'the Tenders module is unaffected, and you can re-activate it here.',
    )) return;

    setDeactivating(true);
    setLifecycleError(null);
    const res = await organisationService.setBillValidationActive(id, activating);
    setDeactivating(false);
    if (!res.success) { setLifecycleError(res.error ?? 'Update failed'); return; }
    await load();
  };

  const handleDeleteContact = async (cid: string) => {
    if (!confirm('Remove this contact?')) return;
    await organisationService.deleteContact(cid);
    setContacts((prev) => prev.filter((c) => c.id !== cid));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sites[0]) return;
    setUploading(true);
    const r = await attachmentService.upload(file, { siteId: sites[0].id, category: uploadCategory });
    if (r.success && r.data) setAttachments((prev) => [r.data!, ...prev]);
    setUploading(false);
    e.target.value = '';
  };

  const handleDownloadBillPdf = async (bill: BvBill, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!reportRef.current) return;
    setDownloadingBillId(bill.id);
    try {
      const { downloadReportAsPdf } = await import('../utils/pdfUtils');
      const target = (reportRef.current.querySelector('[data-pdf-target]') ?? reportRef.current) as HTMLElement;
      await downloadReportAsPdf(
        target,
        `BillValidation_${bill.site?.nmi ?? bill.id}.pdf`,
      );
    } catch { /* silent */ }
    finally { setDownloadingBillId(null); }
  };

  const handleDeleteAttachment = async (a: BvAttachment) => {
    if (!confirm(`Delete "${a.file_name}"?`)) return;
    await attachmentService.delete(a.id, a.storage_path);
    setAttachments((prev) => prev.filter((x) => x.id !== a.id));
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-28 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-64 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <Building2 className="h-14 w-14 mb-4 opacity-20" />
        <p className="text-base font-medium mb-1">{error ?? 'Client not found'}</p>
        <Button variant="link" onClick={() => navigate(back.to)}>
          {back.label}
        </Button>
      </div>
    );
  }

  const bv = org.extn?.bv;
  const enrolledSites = sites.filter((s) => s.bv_extn?.bill_val_status === 'Enrolled').length;
  const primaryContact = contacts.find((c) => c.bv_extn?.is_primary);

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb — back to wherever the user came from (job 16) */}
      <button
        onClick={() => navigate(back.to)}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {back.label}
      </button>

      <div className="space-y-6">

        {/* ── Header card ──────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="bg-primary/10 p-3 rounded-lg flex-shrink-0">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-2xl md:text-3xl">{org.name}</CardTitle>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-2">
                    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_STYLES[org.status] ?? STATUS_STYLES.lead)}>
                      {org.status.charAt(0).toUpperCase() + org.status.slice(1)}
                    </span>
                    {org.abn && <span>ABN {org.abn}</span>}
                    {isClientShielded(org) && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Shield className="h-3 w-3" /> Shield Platinum
                      </span>
                    )}
                    <span>Created {new Date(org.created_at).toLocaleDateString('en-AU')}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 md:flex-nowrap md:flex-shrink-0">
                {bv?.google_drive_link && (
                  <Button variant="outline" className="flex items-center gap-2" asChild>
                    <a href={bv.google_drive_link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Drive
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  onClick={() => navigate(`/bill-validation/organisations/${org.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
                <Button
                  className="flex items-center gap-2"
                  onClick={() => navigate(`/bill-validation/sites/new?org=${org.id}`)}
                >
                  <Plus className="h-4 w-4" />
                  Add Site
                </Button>
                {/* Job 28: lifecycle controls. Deactivate first — it is the
                    reversible one and the right answer nearly every time. */}
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  disabled={deactivating}
                  onClick={() => void handleToggleActive()}
                  title={org.bill_val === 'active'
                    ? 'Hide from Bill Validation without deleting anything'
                    : 'Show in Bill Validation again'}
                >
                  {deactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {org.bill_val === 'active' ? 'Deactivate' : 'Re-activate'}
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={openDeleteDialog}
                  title="Permanently delete this client"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* ── Metrics row ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Sites', value: sites.length, sub: `${enrolledSites} enrolled` },
            { label: 'Contacts', value: contacts.length, sub: primaryContact ? `${primaryContact.first_name} ${primaryContact.last_name} (primary)` : 'No primary set' },
            { label: 'Attachments', value: attachments.length, sub: 'Uploaded files' },
            { label: 'Status', value: org.status.charAt(0).toUpperCase() + org.status.slice(1), sub: '' },
          ].map((m) => (
            <Card key={m.label}>
              <CardContent className="pt-5">
                <p className="text-sm font-medium text-muted-foreground">{m.label}</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">{m.value}</h3>
                {m.sub && <p className="text-xs text-muted-foreground mt-1">{m.sub}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Details + BV Config ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" />Client Details</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Client Name" value={org.name} />
                <DetailRow label="ABN" value={org.abn} />
                <DetailRow label="SF Account ID" value={org.sf_account_id} />
                <DetailRow label="Status" value={org.status} />
                <DetailRow label="Created" value={new Date(org.created_at).toLocaleDateString('en-AU')} />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="h-5 w-5 text-primary" />BV Configuration</CardTitle></CardHeader>
            <CardContent>
              <dl>
                <DetailRow label="Shield Platinum" value={isClientShielded(org) ? 'Yes' : 'No'} />
                <DetailRow label="Parent Org ID" value={bv?.parent_org_id} />
                <DetailRow label="Broker ID" value={bv?.broker_id} />
              </dl>
              {/* Drive link lives in the header action bar next to the client name —
                  repeating it here just gave the same shortcut two homes. */}
            </CardContent>
          </Card>
        </div>

        {/* ── Contacts ─────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Contacts ({contacts.length})
              </CardTitle>
              <Button onClick={() => setContactDialog({ open: true, editing: null })} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Contact
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {contacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Users className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm">No contacts yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Type</th>
                      <th className="px-6 py-3">Email</th>
                      <th className="px-6 py-3">Phone</th>
                      <th className="px-6 py-3">Mobile</th>
                      <th className="px-6 py-3">Role</th>
                      <th className="px-6 py-3 w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {contacts.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50/50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{c.first_name} {c.last_name}</span>
                            {c.bv_extn?.is_primary && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                                Primary
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {c.bv_extn?.contact_type ?? '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">{c.email ?? '—'}</td>
                        <td className="px-6 py-4 text-muted-foreground">{c.phone ?? '—'}</td>
                        <td className="px-6 py-4 text-muted-foreground">{c.bv_extn?.mobile ?? '—'}</td>
                        <td className="px-6 py-4 text-muted-foreground">{c.role ?? '—'}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-1.5">
                            <button onClick={() => setContactDialog({ open: true, editing: c })}
                              className="p-1.5 rounded hover:bg-gray-100 text-muted-foreground hover:text-gray-700">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleDeleteContact(c.id)}
                              className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Sites ────────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Sites ({sites.length})
              </CardTitle>
              <Button onClick={() => navigate(`/bill-validation/sites/new?org=${org.id}`)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Site
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {sites.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <MapPin className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm">No sites linked to this client</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-3">NMI</th>
                      <th className="px-6 py-3">Address</th>
                      <th className="px-6 py-3">State</th>
                      <th className="px-6 py-3">Distributor</th>
                      <th className="px-6 py-3">Tariff Code</th>
                      <th className="px-6 py-3">TNI</th>
                      <th className="px-6 py-3">DLF</th>
                      <th className="px-6 py-3">Enrolment Status</th>
                      <th className="px-6 py-3">Enrolled Date</th>
                      <th className="px-6 py-3">Embedded</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sites.map((s) => {
                      const status = s.bv_extn?.bill_val_status ?? 'Not Enrolled';
                      return (
                        <tr key={s.id} onClick={() => navigate(`/bill-validation/sites/${s.id}`)}
                          className="hover:bg-blue-50/30 cursor-pointer transition-colors">
                          <td className="px-6 py-4 font-mono text-sm font-semibold text-gray-900">{s.nmi ?? '—'}</td>
                          <td className="px-6 py-4 text-muted-foreground">
                            {[s.addr_street, s.addr_city].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td className="px-6 py-4 text-muted-foreground">{s.bv_extn?.state ?? s.addr_state ?? '—'}</td>
                          <td className="px-6 py-4 text-muted-foreground">{s.bv_extn?.network_distributor ?? '—'}</td>
                          <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{s.bv_extn?.network_tariff_code ?? '—'}</td>
                          <td className="px-6 py-4 text-muted-foreground">{s.bv_extn?.tni ?? '—'}</td>
                          <td className="px-6 py-4 text-muted-foreground">{s.bv_extn?.dlf_code ?? '—'}</td>
                          <td className="px-6 py-4">
                            <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', SITE_STATUS_STYLES[status] ?? SITE_STATUS_STYLES['Not Enrolled'])}>
                              {status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-xs text-muted-foreground">
                            {s.bv_extn?.enrolment_date ? new Date(s.bv_extn.enrolment_date).toLocaleDateString('en-AU') : '—'}
                          </td>
                          <td className="px-6 py-4 text-xs text-muted-foreground">{s.bv_extn?.is_embedded ? 'Yes' : 'No'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Attachments ──────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Paperclip className="h-5 w-5 text-primary" />
                Attachments ({attachments.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Select value={uploadCategory} onValueChange={(v) => setUploadCategory(v as BvAttachmentCategory)}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ATTACHMENT_CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label>
                  <Button asChild disabled={uploading || sites.length === 0}>
                    <span className="flex items-center gap-2 cursor-pointer">
                      <Upload className="h-4 w-4" />
                      {uploading ? 'Uploading…' : 'Upload File'}
                    </span>
                  </Button>
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={sites.length === 0} />
                </label>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {sites.length === 0 && (
              <p className="text-sm text-amber-700 px-6 py-3 bg-amber-50 border-b border-amber-100">
                Add a site first to upload attachments.
              </p>
            )}
            {attachments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Paperclip className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm">No attachments yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                      <th className="px-6 py-3">File Name</th>
                      <th className="px-6 py-3">Category</th>
                      <th className="px-6 py-3">Size</th>
                      <th className="px-6 py-3">LOA Expiry</th>
                      <th className="px-6 py-3">Uploaded</th>
                      <th className="px-6 py-3 w-28" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {attachments.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50/50">
                        <td className="px-6 py-4 font-medium text-gray-900">{a.file_name}</td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {ATTACHMENT_CATEGORY_LABELS[a.category]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-muted-foreground text-xs">
                          {a.file_size_bytes ? `${(a.file_size_bytes / 1024).toFixed(0)} KB` : '—'}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground text-xs">
                          {a.loa_expiry_date ? new Date(a.loa_expiry_date).toLocaleDateString('en-AU') : '—'}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground text-xs">
                          {new Date(a.created_at).toLocaleDateString('en-AU')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <button onClick={async () => { const url = await attachmentService.getSignedUrl(a.storage_path); if (url) window.open(url, '_blank'); }}
                              className="text-sm text-primary hover:underline">Open</button>
                            <button onClick={() => handleDeleteAttachment(a)}
                              className="p-1 hover:text-red-600 text-muted-foreground">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Reports ──────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Reports ({orgBills.filter(b => b.shadow_result && b.actual_parsed_result).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {reportsLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading reports…
              </div>
            ) : orgBills.filter(b => b.shadow_result && b.actual_parsed_result).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FileText className="h-10 w-10 mb-3 opacity-20" />
                <p className="text-sm">No validated reports for this client yet.</p>
                <p className="text-xs text-gray-400 mt-1">Parse invoices on bills to generate reports.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {orgBills
                  .filter(b => b.shadow_result && b.actual_parsed_result)
                  .map(bill => {
                    const sr = bill.shadow_result as ShadowBillResult;
                    const ar = effectiveInvoiceResult(bill) as ActualBillResult;
                    // The same verdict and headline as every other screen (billVerdict).
                    const verdict = verdictFor(bill);
                    const expected = verdict?.expectedTotal ?? sr.total;
                    const diff = verdict?.variance ?? ar.total - sr.total;
                    const isOver = verdict ? verdict.bucket === 'overcharge' : diff > 0.5;
                    const isUnder = verdict ? verdict.bucket === 'undercharge' : diff < -0.5;
                    const isExpanded = expandedBillId === bill.id;
                    const isDownloading = downloadingBillId === bill.id;
                    const fmt = (n: number) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

                    return (
                      <div key={bill.id}>
                        <div
                          onClick={() => setExpandedBillId(prev => prev === bill.id ? null : bill.id)}
                          className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-gray-400 shrink-0">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900">{bill.site?.nmi ?? '—'}</span>
                              <span className="text-gray-300">·</span>
                              <span className="text-sm text-gray-500">
                                {new Date(bill.bill_from).toLocaleDateString('en-AU')} – {new Date(bill.bill_to).toLocaleDateString('en-AU')}
                              </span>
                            </div>
                            {bill.report_data?.generated_at && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Generated {new Date(bill.report_data.generated_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                          <div className="text-right hidden md:block min-w-[90px]">
                            <p className="text-xs text-gray-400">Expected</p>
                            <p className="text-sm font-medium text-gray-700">{fmt(expected)}</p>
                          </div>
                          <div className="text-right hidden md:block min-w-[90px]">
                            <p className="text-xs text-gray-400">Actual</p>
                            <p className="text-sm font-medium text-gray-700">{fmt(ar.total)}</p>
                          </div>
                          <div className="text-right min-w-[90px]">
                            <p className="text-xs text-gray-400">Variance</p>
                            <p className={`text-sm font-semibold ${isOver ? 'text-red-600' : isUnder ? 'text-yellow-600' : 'text-green-600'}`}>
                              {diff >= 0 ? '+' : ''}{fmt(diff)}
                            </p>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${isOver ? 'bg-red-100 text-red-700' : isUnder ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {isOver ? <TrendingUp className="h-3 w-3" /> : isUnder ? <TrendingDown className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                            {isOver ? 'Overcharge' : isUnder ? 'Undercharge' : 'Matched'}
                          </span>
                          {isExpanded && (
                            <button
                              onClick={e => void handleDownloadBillPdf(bill, e)}
                              disabled={isDownloading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors shrink-0"
                            >
                              {isDownloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                              {isDownloading ? 'Generating…' : 'Download PDF'}
                            </button>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-100" ref={expandedBillId === bill.id ? reportRef : undefined}>
                            <BillReportScreen bill={bill} shadowResult={sr} actualResult={ar} />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>

      </div>{/* end space-y-6 */}

      <ContactDialog
        open={contactDialog.open}
        onClose={() => setContactDialog({ open: false, editing: null })}
        clientId={org.id}
        editing={contactDialog.editing}
        onSaved={() => organisationService.listContacts(org.id).then((r) => { if (r.success) setContacts(r.data ?? []); })}
      />

      {/* Job 28: delete a client — for test entities, not for ones with history */}
      <Dialog open={confirmDelete} onOpenChange={(v) => !v && !deleting && setConfirmDelete(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete this client?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-gray-700">
              <span className="font-medium">{org.name}</span> will be permanently removed,
              along with its sites and contacts. This cannot be undone.
            </p>

            {deps == null ? (
              <p className="text-xs text-gray-400 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking what references this client…
              </p>
            ) : (
              <ul className="text-xs text-gray-600 space-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <li>Bills: <span className="font-semibold">{deps.bills}</span>{deps.bills > 0 && ' — deletion is blocked while bills exist'}</li>
                <li>Sites: <span className="font-semibold">{deps.sites}</span>{deps.sites > 0 && ' — will be deleted with the client'}</li>
                <li>Contacts: <span className="font-semibold">{deps.contacts}</span>{deps.contacts > 0 && ' — will be deleted with the client'}</li>
              </ul>
            )}

            {deps != null && deps.bills > 0 && (
              <p className="text-xs text-gray-600">
                Use <span className="font-medium">Deactivate</span> instead — it hides the client
                from Bill Validation and keeps its history.
              </p>
            )}

            {lifecycleError && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{lifecycleError}</p>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
            <Button
              onClick={() => void handleDelete()}
              disabled={deleting || deps == null || deps.bills > 0}
              className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleting ? 'Deleting…' : 'Delete Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
